import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Grid,
  Avatar,
  IconButton,
  Divider,
  CircularProgress
} from "@mui/material";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScaleIcon from "@mui/icons-material/Scale";
import PhoneIcon from "@mui/icons-material/Phone";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import apiClient from "../../Utils/apiClient";

const DetailRow = ({ icon, label, value, valueColor = "text.primary" }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={1.5} borderBottom="1px solid #f0f0f0">
    <Box display="flex" alignItems="center" gap={1.5}>
      {icon}
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight={600} color={valueColor} textAlign="right">
      {value || "N/A"}
    </Typography>
  </Box>
);

const CargoEscortRequestCard = ({ open, onClose, data }) => {
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);

  const payload = data?.data || data || {};

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

const handleApprove = async () => {
    setLoadingApprove(true);
    try {
      // Use the correct update-status route and pass "ADMIN_APPROVED" in the body
      await apiClient.put(`/cargo-escort/update-status/${payload._id}`, {
        status: "ADMIN_APPROVED"
      });
      console.log("Approved successfully!");
      onClose(); // Closes modal & stops siren
    } catch (error) {
      console.error("Error approving:", error);
      alert("Failed to approve. Please try again.");
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleReject = async () => {
    setLoadingReject(true);
    try {
      // Use the correct update-status route and pass "REJECTED" in the body
      await apiClient.put(`/cargo-escort/update-status/${payload._id}`, {
        status: "REJECTED"
      });
      console.log("Rejected successfully!");
      onClose(); // Closes modal & stops siren
    } catch (error) {
      console.error("Error rejecting:", error);
      alert("Failed to reject. Please try again.");
    } finally {
      setLoadingReject(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { 
          borderRadius: "16px", 
          overflow: "hidden", 
          backgroundColor: "#fafbfc",
          maxHeight: "90vh"
        }
      }}
    >
      {/* 1. TOP ALERT BANNER */}
      <Box sx={{ backgroundColor: "#edf2ff", p: 2.5, display: "flex", alignItems: "flex-start", flexShrink: 0 }}>
        <Box sx={{ color: "#2563eb", mr: 2, mt: 0.5 }}>
          <NotificationsActiveIcon fontSize="large" />
        </Box>
        <Box flex={1}>
          <Typography variant="h6" color="#2563eb" fontWeight={700} gutterBottom>
            New Load Alert!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Typography component="span" fontWeight={600} color="#2563eb">
              {payload.loadId || "Unknown ID"}
            </Typography>{" "}
            has been submitted and is awaiting your review.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#64748b" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* 2. MAIN CONTENT AREA */}
      <DialogContent dividers sx={{ p: { xs: 2, sm: 4 }, backgroundColor: "#ffffff" }}>
        
        {/* TOP SUMMARY ROW */}
        <Grid container spacing={3} alignItems="center" mb={4}>
          <Grid item xs={12} sm={3}>
            <Typography variant="h5" color="#2563eb" fontWeight={700}>
              {payload.loadId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              #{payload._id?.slice(-6).toUpperCase() || "N/A"}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={5}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Inventory2OutlinedIcon sx={{ color: "#8b5cf6", fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>{payload.cargoType || "N/A"}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <LocationOnIcon sx={{ color: "#22c55e", fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">{payload.pickupLocation}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <LocationOnIcon sx={{ color: "#ef4444", fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">{payload.deliveryLocation}</Typography>
            </Box>
            <Typography variant="body2" fontWeight={600} color="text.secondary" ml={3.5}>
              Value: ₦{payload.cargoValue || 0}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={2} sx={{ borderLeft: { sm: "1px solid #e2e8f0" }, pl: { sm: 3 } }}>
            <Typography variant="body1" fontWeight={600}>
              {payload.vehicleNumber || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {formatDate(payload.departureDate)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTime(payload.departureDate)}
            </Typography>
          </Grid>
        </Grid>

        {/* DETAILS CARDS */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", p: 3, height: "100%" }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Load Details</Typography>
              
              <DetailRow 
                icon={<Inventory2OutlinedIcon sx={{ color: "#8b5cf6", fontSize: 20 }} />} 
                label="Cargo Type" 
                value={payload.cargoType} 
              />
              <DetailRow 
                icon={<ScaleIcon sx={{ color: "#3b82f6", fontSize: 20 }} />} 
                label="Cargo Value" 
                value={`₦${payload.cargoValue || 0}`} 
              />
              <DetailRow 
                icon={<LocationOnIcon sx={{ color: "#22c55e", fontSize: 20 }} />} 
                label="Pickup Location" 
                value={payload.pickupLocation} 
              />
              <DetailRow 
                icon={<LocationOnIcon sx={{ color: "#ef4444", fontSize: 20 }} />} 
                label="Destination" 
                value={payload.deliveryLocation} 
              />
              <DetailRow 
                icon={<PhoneIcon sx={{ color: "#22c55e", fontSize: 20 }} />} 
                label="Receiver Phone" 
                value={payload.receiverPhoneWithCountryCode || payload.receiverPhone} 
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" fontWeight={700} mb={3}>Driver Details</Typography>
              
              <Box display="flex" flexDirection="column" alignItems="center" mb={4} flex={1}>
                <Avatar 
                  src={payload.DriverImage} 
                  sx={{ width: 80, height: 80, mb: 2, border: "3px solid #eff6ff" }}
                >
                  <PersonOutlineIcon fontSize="large" />
                </Avatar>
                <Typography variant="body1" fontWeight={700} fontSize="1.1rem">
                  {payload.DriverName || "No Driver Assigned"}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {payload.DriverPhoneNo || "N/A"}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Truck</Typography>
                <Typography variant="body2" fontWeight={600}>{payload.vehicleNumber || "N/A"}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {/* 3. BOTTOM ACTION FOOTER */}
      <Box sx={{ p: 3, backgroundColor: "#ffffff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        
        <Box display="flex" alignItems="center" gap={1.5} sx={{ backgroundColor: "#eff6ff", px: 2, py: 1.5, borderRadius: "8px" }}>
          <InfoOutlinedIcon sx={{ color: "#2563eb" }} />
          <Typography variant="body2" color="text.primary">
            Please review all details and take action.
          </Typography>
        </Box>

        {/* UPGRADED BUTTONS HERE */}
        <Box display="flex" gap={2}>
          <Button 
            variant="outlined" 
            onClick={handleReject}
            disabled={loadingReject || loadingApprove}
            startIcon={loadingReject ? <CircularProgress size={20} color="inherit" /> : <CloseIcon />}
            sx={{ 
              px: 4, py: 1.2, 
              fontWeight: 600, 
              textTransform: "none", 
              borderRadius: "8px",
              color: "#dc2626", // Explicit strong red text
              borderColor: "#ef4444", // Explicit red border
              "&:hover": { 
                backgroundColor: "#fef2f2", 
                borderColor: "#dc2626" 
              },
              "&.Mui-disabled": { // Fallback if they are loading
                color: "#fca5a5", 
                borderColor: "#fecaca" 
              }
            }}
          >
            {loadingReject ? "Rejecting..." : "Reject"}
          </Button>

          <Button 
            variant="contained" 
            onClick={handleApprove}
            disabled={loadingReject || loadingApprove}
            startIcon={loadingApprove ? <CircularProgress size={20} color="inherit" /> : <PersonOutlineIcon />}
            sx={{ 
              px: 4, py: 1.2, 
              fontWeight: 600, 
              textTransform: "none", 
              borderRadius: "8px",
              backgroundColor: "#1d4ed8", // Explicit strong blue
              color: "#ffffff",
              "&:hover": { 
                backgroundColor: "#1e40af" 
              },
              "&.Mui-disabled": { // Fallback if they are loading
                backgroundColor: "#93c5fd",
                color: "#ffffff"
              }
            }}
          >
            {loadingApprove ? "Processing..." : "Approve & Assign"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CargoEscortRequestCard;